import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Icon from 'dpl/common/components/Icon';
import Button from 'dpl/common/design_system/Button';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import GoogleMapContainer from 'dpl/containers/GoogleMapContainer';

import mapRadiusInner from 'dpl/assets/search_alerts/map-radius-inner.svg';
import mapRadiusOuter from 'dpl/assets/search_alerts/map-radius-outer.svg';

import { SearchAlertsMapBanner as SearchAlertsMapBannerData } from '../../hooks/useSearchAlertsMapBanner';

const CARD_SHADOW = '0 2px 16px 0 rgba(0, 0, 0, 0.04)';
const ICON_SIZE = '18px';
const MAP_HEIGHT = 166;
// The radius circles darken the map under them, as in the designs.
// plus-darker is Safari-only so far; multiply is the closest elsewhere.
const RADIUS_BLEND_MODE =
  typeof CSS !== 'undefined' && CSS.supports?.('mix-blend-mode', 'plus-darker')
    ? 'plus-darker'
    : 'multiply';

// The banner is a picture of the region, not a map to use.
function lockMap(map: any) {
  map.setOptions({
    gestureHandling: 'none',
    keyboardShortcuts: false,
    clickableIcons: false
  });
}

function formatCount(count: number) {
  return count.toLocaleString('en-US');
}

function bodyCopy(breederCount: number, breedName: string) {
  if (breederCount === 0) {
    return `Get notified when a ${breedName} breeder in your region adds a new litter.`;
  }
  if (breederCount === 1) {
    return `There is 1 ${breedName} breeder in your region. Get notified when a new litter is added.`;
  }
  return `There are ${formatCount(breederCount)} ${breedName} breeders in your region. Get notified when a new litter is added.`;
}

interface SearchAlertsMapBannerProps {
  banner: SearchAlertsMapBannerData;
  locationName: string;
  showSuccessContent: boolean;
  successSubtitle: string;
  onGetAlertsClick: () => void;
}

/**
 * Map alerts banner (CORE-6678): the variant of exp_search_alerts_banner.
 * Opens the same signup flow as today's banner.
 */
export default function SearchAlertsMapBanner({
  banner: { isLoading, breederCount, latitude, longitude },
  locationName,
  showSuccessContent,
  successSubtitle,
  onGetAlertsClick
}: SearchAlertsMapBannerProps) {
  const { breedName } = useSelector(({ server }: any) => server);

  const coordinates = useMemo(
    () => [{ lat: Number(latitude), lng: Number(longitude) }],
    [latitude, longitude]
  );

  return (
    <div className="SearchAlertsMapBanner bg-sand nh5 pa5">
      <div className="bg-white br4 pa4" style={{ boxShadow: CARD_SHADOW }}>
        <div className="flex items-center gap-8 mb3">
          <Icon name="gooddogavatar" width={ICON_SIZE} height={ICON_SIZE} />
          <p className="font-14 tertiary" style={{ lineHeight: 1.3 }}>
            National network of trusted breeders
          </p>
        </div>
        {isLoading || breederCount === null ? (
          <SkeletonBlock className="br3 mb3" height={`${MAP_HEIGHT}px`} />
        ) : (
          <div
            className="relative br3 overflow-hidden bg-sand mb3"
            style={{ height: MAP_HEIGHT }}
            aria-hidden="true"
          >
            <GoogleMapContainer
              coordinates={coordinates}
              onMapCreate={lockMap}
            />
            <div
              className="absolute absolute--fill"
              style={{ pointerEvents: 'none' }}
            >
              <img
                src={mapRadiusOuter}
                alt=""
                width={166}
                height={166}
                className="absolute"
                style={{
                  left: 'calc(50% - 83.5px)',
                  top: -0.5,
                  mixBlendMode: RADIUS_BLEND_MODE
                }}
              />
              <img
                src={mapRadiusInner}
                alt=""
                width={104}
                height={104}
                className="absolute"
                style={{
                  left: 'calc(50% - 52.5px)',
                  top: 31,
                  mixBlendMode: RADIUS_BLEND_MODE
                }}
              />
              <div
                className="absolute flex items-center gap-4 bg-blue-500 white br-pill pv1"
                style={{
                  left: '50%',
                  top: 'calc(50% + 4px)',
                  transform: 'translate(-50%, -50%)',
                  paddingLeft: 5,
                  paddingRight: 5
                }}
              >
                <Icon name="fetch-pin" width="16px" height="16px" />
                <span
                  className="font-12 fw-medium nowrap"
                  style={{ lineHeight: 1.2 }}
                >
                  {locationName}
                </span>
              </div>
            </div>
          </div>
        )}
        <h4
          className="font-24 mb2"
          style={{ lineHeight: 1.2, letterSpacing: '-0.24px' }}
        >
          {showSuccessContent
            ? 'You’re signed up'
            : 'Never miss a litter near you'}
        </h4>
        <p className="font-14 secondary mb3" style={{ lineHeight: 1.3 }}>
          {showSuccessContent
            ? successSubtitle
            : breederCount !== null && bodyCopy(breederCount, breedName)}
        </p>
        {showSuccessContent ? (
          <div className="dib tertiary font-14 fw-medium pv2 ph4 ba b--default br2">
            Alerts enabled
          </div>
        ) : (
          <Button
            size="tiny"
            className="font-14"
            type="button"
            onClick={onGetAlertsClick}
          >
            Get alerts
          </Button>
        )}
      </div>
    </div>
  );
}
